import { collection, isMac, keybinding } from './common';
import {
  type Command,
  type EditorState,
  history,
  Plugin,
  PluginKey,
  redo,
  undo,
} from './pm';

/**
 * One redo chord, several, or `false` for none.
 *
 * Platforms disagree on the convention — `Mod-y` on Windows and Linux,
 * `Mod-Shift-z` on macOS — so an editor may bind more than one. The default
 * binds `Mod-Shift-z` everywhere and keeps `Mod-y` off macOS.
 */
export type HistoryRedoKeys = string | false | Array<string | false>;

export type HistoryConfig = {
  depth?: number;
  newGroupDelay?: number;
  // keys
  keyUndo?: string | false;
  keyRedo?: HistoryRedoKeys;
};

/**
 * The chord options only. The remaining knobs describe how
 * prosemirror-history groups edits and therefore do not apply to an editor
 * whose history is owned by another implementation.
 */
export type HistoryKeysConfig = Pick<HistoryConfig, 'keyUndo' | 'keyRedo'>;

/**
 * The undo/redo implementation an editor's history is backed by.
 *
 * An editor installs exactly one history implementation, but not always the
 * same one: local notes undo through prosemirror-history, collaborative notes
 * undo through Yjs. Registering the pair in editor state lets the undo/redo
 * chords — and anything else that must reach the active history, such as the
 * nested math source editor — work without assuming which one is installed.
 */
export type HistoryCommands = {
  undo: Command;
  redo: Command;
};

type RequiredConfig = Required<HistoryConfig>;

const DEFAULT_CONFIG: RequiredConfig = {
  depth: 100,
  newGroupDelay: 500,
  keyUndo: 'Mod-z',
  keyRedo: isMac ? ['Mod-Shift-z'] : ['Mod-y', 'Mod-Shift-z'],
};

const historyCommandsKey = new PluginKey<HistoryCommands>('history-commands');

/**
 * The history commands registered on `state`, or `undefined` when the editor
 * was built without a history collection.
 */
export function getHistoryCommands(
  state: EditorState,
): HistoryCommands | undefined {
  return historyCommandsKey.getState(state);
}

/**
 * prosemirror-history backed undo/redo: the history plugin itself, the command
 * registry it is reached through, and the undo/redo chords.
 */
export function setupHistory(userConfig?: HistoryConfig) {
  const config = {
    ...DEFAULT_CONFIG,
    ...userConfig,
  };

  const plugin = {
    history: pluginHistory(config),
    commands: pluginCommands({ undo, redo }),
    keybindings: pluginKeybindings(config),
  };

  return collection({
    id: 'history',
    plugin,
  });
}

/**
 * The command registry and undo/redo chords for an editor whose history is
 * owned elsewhere — a collaborative note, where Yjs' UndoManager replaces
 * prosemirror-history.
 *
 * Deliberately installs no history plugin of its own: two independent undo
 * stacks over the same document would each be unaware of the other's edits.
 */
export function setupHistoryFor(
  commands: HistoryCommands,
  userConfig?: HistoryKeysConfig,
) {
  const config = {
    ...DEFAULT_CONFIG,
    ...userConfig,
  };

  const plugin = {
    commands: pluginCommands(commands),
    keybindings: pluginKeybindings(config),
  };

  return collection({
    id: 'history',
    plugin,
  });
}

// PLUGINS
function pluginHistory(config: RequiredConfig) {
  return () => {
    const { depth, newGroupDelay } = config;
    return history({
      depth,
      newGroupDelay,
    });
  };
}

function pluginCommands(commands: HistoryCommands) {
  return () =>
    new Plugin<HistoryCommands>({
      key: historyCommandsKey,
      state: {
        init: () => commands,
        apply: () => commands,
      },
    });
}

function pluginKeybindings(config: RequiredConfig) {
  return () => {
    const undoKey = historyCommand('undo');
    const redoKey = historyCommand('redo');
    const bindings: Array<[string | false, Command]> = [
      [config.keyUndo, undoKey],
      ...redoChords(config.keyRedo).map((key): [string | false, Command] => [
        key,
        redoKey,
      ]),
    ];

    return keybinding(bindings, 'history');
  };
}

/**
 * Resolves a history chord against the registry, so the same chord runs
 * whichever undo/redo implementation the editor installed.
 */
function historyCommand(name: 'undo' | 'redo'): Command {
  return (state, dispatch, view) =>
    getHistoryCommands(state)?.[name](state, dispatch, view) ?? false;
}

/**
 * `false` disables redo the way `keybinding` documents a `false` key. Repeats
 * collapse: `keybinding` rejects two commands on one chord, and the same chord
 * listed twice is one command, not two.
 */
function redoChords(value: HistoryRedoKeys): Array<string | false> {
  if (value === false) {
    return [];
  }

  const chords = typeof value === 'string' ? [value] : value;
  return [...new Set(chords)];
}
